import { useState } from 'react';
import { type Control } from 'react-hook-form';
import { OutlineWarning01Icon } from '@tempmee/react-icons';
import { Box, Button, Column, Row, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { CardSpinner, FloatingContainer, Screen, ZStack } from '~/commonComponents';
import { BackButtonProxy as BackButton } from '~/commonComponents/BackButton';
import { PaymentLockedBanner, SectionCard } from '~/components/ShiftRequestRedesign/common';
import AnimatedCheckCircle from '~/components/shared/AnimatedCheckCircle';
import ConfirmPopup from '~/components/shared/ConfirmPopup';
import { type IShiftDetailsFormValues } from '../ShiftDetails/ShiftDetailsForm/useShiftDetailsForm';
import { AutoConfirmField } from '../AddInvites/AutoConfirmField';
import { DatePickerPopup } from './DatePickerPopup';
import { EditOpenShiftForm } from './EditOpenShiftForm';
import { EditableDatePill } from './EditableDatePill';
import { useEditOpenShift } from './useEditOpenShift';

export const EditOpenShiftScreen = () => {
  const {
    job,
    isLoadingJob,
    officeName,
    officeId,
    professionType,
    preferredSpecialty,
    locked,
    control,
    watch,
    selectedDate,
    setSelectedDate,
    showSuccessPopup,
    showNavigationGuard,
    isSaving,
    handleSave,
    handleSuccessConfirm,
    handleBack,
    navigateToPending,
    clearPendingNavigation,
    setShowNavigationGuard,
  } = useEditOpenShift();

  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  if (isLoadingJob || !job) return <CardSpinner />;

  return (
    <Screen minWidth={768} paddingInline={30} paddingTop={16} paddingBottom={16}>
      <FloatingContainer
        justifyContent="flex-start"
        maxWidth={1120}
        borderRadius={0}
        margin={0}
        minWidth={0}
        width="100%"
        alignSelf="center"
        height="100%"
      >
        <Row align="center" style={{ marginTop: 60, position: 'relative' }}>
          <ZStack left={20}>
            <BackButton onClick={handleBack} />
          </ZStack>
          <Typography as="span" bold size="heading_3" textAlign="center" style={{ flex: 1 }}>
            {job?.isOnsiteInterview ? 'Edit Interview' : 'Edit Shift'}
          </Typography>
        </Row>

        <Column pt={2} style={{ paddingLeft: 28, paddingRight: 28, paddingBottom: 60 }}>
          <Row gap={6} wrap="wrap" style={{ marginTop: 32 }}>
            <Column style={{ flex: 1, minWidth: 430 }}>
              <SectionCard
                title={job?.isOnsiteInterview ? 'Interview Details' : 'Shift Details'}
                testId="edit-open-shift-details-card"
              >
                <EditOpenShiftForm
                  control={control}
                  watch={watch}
                  officeName={officeName}
                  officeId={officeId}
                />
              </SectionCard>
            </Column>

            <Column style={{ flex: 1, minWidth: 430 }}>
              <Box
                data-testid="edit-open-shift-date-card"
                data-cy="edit-open-shift-date-card"
                style={{
                  backgroundColor: colors.shades[0],
                  borderRadius: 8,
                  padding: '20px 28px',
                  border: `1.5px solid ${colors.neutral[100]}`,
                }}
              >
                <Row align="center" justify="between">
                  <Typography as="span" bold size="heading_3" color={colors.neutral[600]}>
                    Date
                  </Typography>
                  <Box style={{ position: 'relative' }}>
                    <EditableDatePill
                      date={selectedDate}
                      onClick={() => setIsDatePickerOpen((v) => !v)}
                    />
                    <DatePickerPopup
                      currentDate={selectedDate}
                      isOpen={isDatePickerOpen}
                      onToggle={() => setIsDatePickerOpen(false)}
                      onConfirm={(date) => {
                        setSelectedDate(date);
                        setIsDatePickerOpen(false);
                      }}
                    />
                  </Box>
                </Row>
              </Box>

              <Box style={{ marginTop: 24 }}>
                <SectionCard title="Speed Up Your Coverage" testId="edit-open-shift-speed-up-card">
                  {locked && (
                    <Box style={{ marginBottom: 16 }}>
                      <PaymentLockedBanner context="edit" />
                    </Box>
                  )}
                  <Box
                    aria-disabled={locked || undefined}
                    style={{
                      opacity: locked ? 0.5 : 1,
                      pointerEvents: locked ? 'none' : 'auto',
                    }}
                  >
                    <AutoConfirmField
                      control={control as unknown as Control<IShiftDetailsFormValues>}
                      professionType={professionType}
                      preferredSpecialty={preferredSpecialty}
                      eventContext="open_shift"
                      disabled={locked}
                    />
                  </Box>
                </SectionCard>
              </Box>
            </Column>
          </Row>

          <Button
            data-testid="save-changes-button"
            data-cy="save-changes-button"
            onClick={handleSave}
            isLoading={isSaving}
            size="lg"
            style={{ marginTop: 40, alignSelf: 'center' }}
          >
            Save Changes
          </Button>
        </Column>
      </FloatingContainer>

      {showNavigationGuard && (
        <ConfirmPopup
          Icon={() => (
            <Box style={{ marginBottom: 20 }}>
              <OutlineWarning01Icon size={80} color={colors.primary[500]} />
            </Box>
          )}
          title="Are You Sure You Want to Leave?"
          description="You've made changes to your shift request that haven't been saved. Do you still want to leave?"
          leftButtonText="Go Back"
          leftButtonAction={() => {
            clearPendingNavigation();
            setShowNavigationGuard(false);
          }}
          rightButtonText="Leave Anyway"
          rightButtonAction={navigateToPending}
          closePopup={() => {
            clearPendingNavigation();
            setShowNavigationGuard(false);
          }}
          dataTestId="navigation-guard-popup"
        />
      )}

      {showSuccessPopup && (
        <ConfirmPopup
          Icon={() => <AnimatedCheckCircle />}
          titleStyle={{ marginTop: 30 }}
          description="The offer has been updated"
          descriptionStyle={{ width: '90%', marginBottom: 32, marginTop: 24 }}
          rightButtonText="Ok"
          rightButtonAction={handleSuccessConfirm}
          closePopup={handleSuccessConfirm}
          dataTestId="success-popup"
        />
      )}
    </Screen>
  );
};
